import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import type { CapKeyQueryParams } from "src/app/features/EmvKernelProfile/subFeatures/CapKey/services/server/CapKeyQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { useCapKeyUpdateMutation, useLazyCapKeyLoadQuery } from "../../services/server/CapKeyApi";
import type { CapKeyEntity } from "../../types/CapKeyEntity";

export const CapKeyEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { emvKernelProfileId, id } = useParams();
    const [queryConfig, setQueryConfig] = useState<CapKeyQueryParams>({});

    // set save params
    useEffect(() => {
        setQueryConfig({ emvKernelProfileId, id });
    }, [emvKernelProfileId, id]);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("cap-key");
    const [loadFormData, { isFetching: loadFormIsInProgress }] = useLazyCapKeyLoadQuery();

    const [formData, setFormData] = useState<CapKeyEntity>({} as CapKeyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    // load form data
    useEffect(() => {
        if (id && queryConfig.emvKernelProfileId) {
            loadFormData(queryConfig).then((loadedData) => {
                if (loadedData.data) {
                    setFormData(loadedData.data);
                }
            });
        }
    }, [id, loadFormData, queryConfig]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCapKeyUpdateMutation();
    const completeSubmit = useFormSubmitCompletion(
        formAction,
        `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${emvKernelProfileId}`,
    );
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ capKeyFormData: formData, capKeyQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.id,
                        resource: "CapKey",
                    }),
                );
            }
        },
        [formData, saveFormData, queryConfig, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CapKeyEntity>
                    name="CapKeyEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
